import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { formatRedirectURL } from '@shared/utils/formatting/redirectURL.formatter';
import { getIsAllowListedUrl } from '@shared/components/SafeLink/SafeLink.utils';

type UseValidateHasSafeLinkReturn = [<T extends string | URL>(href: T) => { isSafeLink: boolean; url: T }, boolean];

/**
 * Custom hook that returns a function to validate and transform a URL based on feature flags.
 *
 * It determines whether a given URL is considered "safe", and optionally transforms it by:
 * - Ensuring it uses HTTPS,
 * - Redirecting it through a redirect service (if enabled).
 *
 * @returns {UseValidateHasSafeLinkReturn}
 */
export const useValidateHasSafeLink = (): UseValidateHasSafeLinkReturn => {
  const [safeLinksEnabled, safeLinksLoading] = useIsFeatureEnabled('guestSiteSafeLinks');
  const [guestLinkUseRedirectServiceEnabled, guestLinkUseRedirectServiceLoading] = useIsFeatureEnabled('guestLinkUseRedirectService');

  const loadingFeatureFlags = safeLinksLoading || guestLinkUseRedirectServiceLoading;

  const validateHasSafeLink = useEventCallback(<T extends string | URL>(href: T): { isSafeLink: boolean; url: T } => {
    const linkWithProtocol = typeof href === 'string' ? href : href.toString();

    const isAllowListed = getIsAllowListedUrl(linkWithProtocol);
    const isSafeLink = isAllowListed || !safeLinksEnabled;

    const linkWithRedirectService = formatRedirectURL({ url: linkWithProtocol }) ?? linkWithProtocol;

    const finalUrlStr = isSafeLink && guestLinkUseRedirectServiceEnabled ? linkWithRedirectService : linkWithProtocol;

    let finalUrl: T;

    if (typeof href === 'string') {
      finalUrl = finalUrlStr as T;
    } else {
      try {
        finalUrl = new URL(finalUrlStr) as T;
      } catch {
        finalUrl = href;
      }
    }

    return { isSafeLink, url: finalUrl };
  });

  return [validateHasSafeLink, loadingFeatureFlags];
};
